// This component needs to check for existing plaid credentials
// If credentials and institutions exist, render the drawer which lets them select
// an institution from the list OR "Add new financial institution" button which triggers PlaidLink Login again
// If credentials or institutions do not exist, render the PlaidLink login button

import { useState, useEffect, useCallback } from 'react'
import styled from '@emotion/styled'
import { usePlaidLink } from 'react-plaid-link'
import _ from 'lodash'
import { inject, observer } from 'mobx-react'
import { API } from '../../../../api'
import { LinkRounded } from '../../../../assets/icons'
import { HelpIcon, TooltipText } from '../../../../components'
import { XSmallTertiaryButton } from '../../../../components/Button'
import { mobileFullWidth } from '../../../../components/buttonStyles'
import {
  CHOICE_MAX_WIDTH,
  CHOICE_STACK_WIDTH,
  HELP_ICON_GUTTER,
  InvestmentChoice,
} from '../AddInvestmentsStyle'
import { ButtonWithHelpIcon } from '../../../../components/styled'
import { MOBILE } from '../../../../guided-toolbox/utils/breakpoints'
import PlaidLinkDrawer from './PlaidLinkDrawer'

const LinkWithHelp = styled(ButtonWithHelpIcon)`
  align-items: flex-start;
  gap: 8px;
  min-width: 0;

  flex: 1 1 ${HELP_ICON_GUTTER}px;
  max-width: calc(${CHOICE_MAX_WIDTH} + ${HELP_ICON_GUTTER}px);

  @container investment-choices (max-width: ${CHOICE_STACK_WIDTH - 1}px) {
    /* Left gutter balances the icon on the right so the button looks centered */
    flex: 0 0 auto;
    max-width: none;
    margin-left: ${HELP_ICON_GUTTER}px;
  }
`

const LinkAction = styled.span`
  display: inline-flex;

  ${MOBILE} {
    display: flex;
    width: 100%;
  }
`

const LinkPill = styled(XSmallTertiaryButton)`
  /* The glyph is inset 5px inside its 30px box, so 5 here reads as the design's 10. */
  gap: 5px;
  min-width: 115px;
  line-height: 22px;

  ${mobileFullWidth};
`

function PlaidLink({ store, onSuccess, accountId, bigButton, planType }) {
  const [state, setState] = useState('preload')
  const [selectedInstitution, setSelectedInstitution] = useState('')
  const [institutionsData, setInstitutionsData] = useState([])
  const [plaidAccounts, setPlaidAccounts] = useState([])
  const [plaidLinkToken, setPlaidLinkToken] = useState('')
  const [drawerActive, setDrawerActive] = useState(false)
  const { getLinkToken } = store

  const { open, exit, ready, error } = usePlaidLink({
    onSuccess: handlePlaidLoginSuccess,
    onExit: err => {
      console.log('Exiting Plaid')
      if (err) {
        console.error(err)
      }
    },
    token: plaidLinkToken,
  })

  const openPlaid = () => {
    if (ready) {
      open()
    }
  }

  const fetchInstitutions = useCallback(async () => {
    try {
      // Used to fetch existing available institutions list available for linking if credentials still valid
      const institutions = await API.get('aggregation/link')
      setInstitutionsData(institutions.data)

      setState('done')
    } catch (err) {
      console.error(err)
      setState('preload-error')
    }
  }, [setInstitutionsData, setState])

  useEffect(() => {
    if (error) {
      console.log('Plaid Error')
      exit()
    }
  }, [error, exit])

  useEffect(() => {
    const fetchPlaidLinkToken = async () => {
      try {
        const token = await getLinkToken()
        setPlaidLinkToken(token)
      } catch (err) {
        console.error(err)
        setState('preload-error')
      }
    }

    fetchInstitutions()
    fetchPlaidLinkToken()
  }, [fetchInstitutions, getLinkToken])

  // Save aggregationAccounts data and pass it to the Drawer for selection
  async function handlePlaidLoginSuccess(token, metadata) {
    try {
      const aggregationAccounts = await API.post('aggregation/link', {
        publicToken: token,
      })
      setPlaidAccounts(aggregationAccounts.data)
      setSelectedInstitution(_.get(metadata, 'institution.name', 'your institution'))
      setDrawerActive(true)
    } catch (err) {
      console.error(err)
      setState('plaid-login-error')
    }
  }

  // Call this after they successfully select the institution and/or account they want to link
  async function handleAccountLinkSuccess() {
    if (typeof onSuccess === 'function') {
      onSuccess()
    }
  }

  // If institutions exist, open the Drawer and have them select from the list
  function handleInstitutionsClick() {
    setDrawerActive(true)
  }

  function handleCloseDrawer() {
    setDrawerActive(false)
  }

  // Return nothing if preloading or fetching institutions failed
  if (state === 'preload' || state === 'preload-error') {
    return null
  }

  // Big-button variant: one control whose click either opens the saved-institution drawer or
  // starts a fresh Plaid login. Shares InvestmentChoice with the other investment-entry choices
  // so the three sit in a row at matching sizes.
  if (bigButton) {
    return (
      <LinkWithHelp>
        <InvestmentChoice
          label='Link account'
          sublabel='import investments'
          onClick={_.isEmpty(institutionsData) ? openPlaid : handleInstitutionsClick}
        />
        <HelpIcon tooltip={TooltipText.plaidHelpContent()} />
        <PlaidLinkDrawer
          active={drawerActive}
          fetchInstitutions={fetchInstitutions}
          institutionsData={institutionsData}
          plaidAccounts={plaidAccounts}
          setPlaidAccounts={setPlaidAccounts}
          onHandleCloseDrawer={handleCloseDrawer}
          onSuccessfulLink={handleAccountLinkSuccess}
          accountId={accountId}
          planType={planType}
          openPlaid={openPlaid}
          selectedInstitution={selectedInstitution}
          setSelectedInstitution={setSelectedInstitution}
          state={state}
          setState={setState}
        />
      </LinkWithHelp>
    )
  }

  // The following are the default buttons
  return (
    <LinkAction>
      <LinkPill
        type='button'
        onClick={_.isEmpty(institutionsData) ? openPlaid : handleInstitutionsClick}>
        <LinkRounded />
        Link
      </LinkPill>
      <PlaidLinkDrawer
        active={drawerActive}
        fetchInstitutions={fetchInstitutions}
        institutionsData={institutionsData}
        plaidAccounts={plaidAccounts}
        setPlaidAccounts={setPlaidAccounts}
        onHandleCloseDrawer={handleCloseDrawer}
        onSuccessfulLink={handleAccountLinkSuccess}
        accountId={accountId}
        planType={planType}
        openPlaid={openPlaid}
        selectedInstitution={selectedInstitution}
        setSelectedInstitution={setSelectedInstitution}
        state={state}
        setState={setState}
      />
    </LinkAction>
  )
}

export default inject('store')(observer(PlaidLink))
